// Bookie · Champagne — variant that renders the standalone HTML
// template (1900s Parisian art-nouveau wedding template:
// champagne #E8DBC1 + ivory + gold #B89968 + soft ink, with
// Mucha-style curvilinear swirls crowning the hero and a soft
// shimmer behind the names; Bodoni Moda display + Cormorant
// Garamond italic body + Allura script accent) inside a
// full-bleed iframe. The HTML lives at
// /templates/bookie-champagne.html and is byte-identical to the
// designer source except for the customizer script embedded
// inline — it reads names/date/venue/etc. from the iframe URL
// and swaps the mocks before first paint.
//
// We pass payload values via the URL because postMessage would
// race the iframe's first paint and the customizer needs to
// run before the IntersectionObserver inside the HTML attaches.

import type { VariantProps } from "./types";
import { getLocale } from "@/lib/i18n";

const TEMPLATE_HTML = "/templates/bookie-champagne.html";

export function BookieChampagneStaticVariant({ t, ctx }: VariantProps) {
  const params = new URLSearchParams();
  if (t.names) params.set("names", t.names);
  if (t.date) {
    // Convert dd.mm.yyyy → ISO yyyy-mm-dd for the customizer.
    const m = t.date.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
    if (m) params.set("date", `${m[3]}-${m[2]}-${m[1]}`);
    else params.set("date", t.date);
  }
  if (t.venue) params.set("venue", t.venue);
  if (t.address) params.set("address", t.address);
  if (t.time) params.set("time", t.time);
  // Demo / preview surfaces leave ctx.slug undefined.
  if (ctx.slug) params.set("slug", ctx.slug);
  if (t.musicTrackId) params.set("audio", "/music/" + t.musicTrackId + ".mp3");
  // Cache buster — bump when the static HTML changes.
  params.set("v", "3");

  // Demo/catalog preview (no real invitation slug): default the template's
  // in-iframe language to the app locale, so taklifla.com previews render in
  // English (and a RU viewer sees Russian). The HTMLs read ?lang= (all) and
  // ?default_lang= (newer premium ones) — set both. Owner-published
  // invitations (ctx.slug set) keep their own default_lang / built-in default.
  if (!ctx.slug) {
    const _loc = getLocale();
    if (!params.has("lang")) params.set("lang", _loc);
    if (!params.has("default_lang")) params.set("default_lang", _loc);
  }
  const src = `${TEMPLATE_HTML}?${params.toString()}`;

  return (
    <iframe
      src={src}
      title={t.names || "Sham Sha'oma"}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100svh",
        border: "none",
        display: "block",
        background: "#F6EFE0",
      }}
    />
  );
}
